import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Maximize2, PhoneOff, Video } from 'lucide-react'
import { lazy, Suspense, useCallback, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/useAuth'
import { endCall, joinCall, messagingKeys, startCall, summaryQuery } from '../api'
import { CallsContext } from '../callContext'
import type { CallJoin } from '../types'
import { IncomingCalls } from './IncomingCalls'

// LiveKit (and its styles) load only when someone joins a call.
const CallRoom = lazy(() => import('./CallRoom'))

interface Active {
  join: CallJoin
  title: string
  video: boolean
  /** Direct calls end for both people when one hangs up. */
  endOnLeave: boolean
}

/**
 * Keeps the current call alive across pages (full screen, or minimised to a bar) and shows incoming
 * calls. Mounted once in the app shell.
 */
export function CallProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('messaging')
  const { status, can } = useAuth()
  const qc = useQueryClient()
  const enabled = status === 'authenticated' && can('messaging.use')
  const summary = useQuery(summaryQuery(enabled))
  const [active, setActive] = useState<Active | null>(null)
  const [minimised, setMinimised] = useState(false)
  const [busy, setBusy] = useState(false)

  const enter = useCallback(
    async (load: () => Promise<CallJoin>, title: string, video: boolean, direct: boolean) => {
      if (busy) return
      setBusy(true)
      try {
        const join = await load()
        setActive({ join, title, video, endOnLeave: direct })
        setMinimised(false)
        void qc.invalidateQueries({ queryKey: messagingKeys.all })
      } catch (e) {
        toast.error(t('Could not join the call'), toApiError(e).message)
      } finally {
        setBusy(false)
      }
    },
    [busy, qc, t],
  )

  const leave = useCallback(
    (endForAll: boolean) => {
      const current = active
      setActive(null)
      if (current && (endForAll || current.endOnLeave)) {
        void endCall(current.join.call.id)
          .catch(() => undefined)
          .finally(() => void qc.invalidateQueries({ queryKey: messagingKeys.all }))
      } else {
        void qc.invalidateQueries({ queryKey: messagingKeys.all })
      }
    },
    [active, qc],
  )

  const value = useMemo(
    () => ({
      available: enabled && (summary.data?.callsAvailable ?? false),
      activeCallId: active?.join.call.id ?? null,
      start: (conversationId: number, video: boolean, title: string, direct: boolean) =>
        enter(() => startCall(conversationId, video), title, video, direct),
      join: (callId: number, title: string, video: boolean, direct: boolean) =>
        enter(() => joinCall(callId), title, video, direct),
    }),
    [enabled, summary.data?.callsAvailable, active, enter],
  )

  return (
    <CallsContext.Provider value={value}>
      {children}
      {enabled ? (
        <IncomingCalls
          calls={summary.data?.calls ?? []}
          activeCallId={active?.join.call.id ?? null}
          onJoin={(call, video) =>
            void value.join(call.id, call.conversation.title, video, call.conversation.kind === 'direct')
          }
        />
      ) : null}
      {busy && !active ? (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-overlay" role="status">
          <span className="flex items-center gap-3 rounded-full bg-surface px-5 py-3 text-sm font-medium text-ink shadow-overlay">
            <Spinner /> {t('Connecting…')}
          </span>
        </div>
      ) : null}
      {active ? (
        <>
          <div
            className={minimised ? 'hidden' : 'fixed inset-0 z-[70] flex flex-col bg-neutral-950'}
            role="dialog"
            aria-modal={!minimised}
            aria-label={t('Call: {{title}}', { title: active.title })}
          >
            <Suspense
              fallback={
                <div className="grid flex-1 place-items-center text-white">
                  <Spinner />
                </div>
              }
            >
              <CallRoom
                join={active.join}
                title={active.title}
                video={active.video}
                canEndForAll={!active.endOnLeave}
                onMinimise={() => setMinimised(true)}
                onLeave={leave}
              />
            </Suspense>
          </div>
          {minimised ? (
            <div className="fixed right-4 bottom-4 z-[65] flex items-center gap-2 rounded-full bg-neutral-900 py-2 pr-2 pl-4 text-sm text-white shadow-overlay">
              <Video className="text-success-400 size-4" aria-hidden="true" />
              <span className="max-w-48 truncate">{active.title}</span>
              <button
                type="button"
                onClick={() => setMinimised(false)}
                className="grid size-9 place-items-center rounded-full bg-white/10 hover:bg-white/20"
                aria-label={t('Back to the call')}
              >
                <Maximize2 className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => leave(false)}
                className="grid size-9 place-items-center rounded-full bg-danger-600 hover:bg-danger-700"
                aria-label={t('Leave the call')}
              >
                <PhoneOff className="size-4" />
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </CallsContext.Provider>
  )
}
